import { notEmpty, Numeric } from '@ezchops/helpers';
import { isEqual } from 'lodash';
import { Attributes, Backup, Barline, Direction, Forward, Note, Print } from '../musicxml/elements';
export function parsePart(part) {
    const sliced = parseMeasures(part.measures);
    const instruments = part.instruments;
    return sliced.map((segmentsMeasures) => {
        const segments = segmentsMeasures.map(aggregateSegment);
        return { segments };
    });
    function aggregateSegment(segmentMeasures) {
        const measures = segmentMeasures.map(aggregateMeasure);
        return segmentMeasures.at(-1)?.repeatEnd
            ? { repeats: 1, signature: segmentMeasures[0].timeSignature, measures }
            : { repeats: 0, signature: segmentMeasures[0].timeSignature, measures };
    }
    function aggregateMeasure(measure) {
        const chords = aggregateTuplets(aggregateChords(measure, instruments)).map((reducedTuplet) => {
            const duration = [
                reducedTuplet.value[0] * reducedTuplet.noteValue[0],
                reducedTuplet.value[1]
            ];
            if (reducedTuplet.noteValue[0] === 1) {
                return reducedTuplet.notes[0].dots > 0
                    ? { duration, notes: reducedTuplet.notes[0].hits, dots: reducedTuplet.notes[0].dots, }
                    : { duration, notes: reducedTuplet.notes[0].hits };
            }
            else {
                return { duration, chords: reducedTuplet.notes.map(({ hits }) => hits) };
            }
        });
        return { notes: chords };
    }
}
function parseMeasures(measureNodes) {
    const finalState = measureNodes.reduce(parseMeasure, { measures: [] });
    // Fix divisions, MuseScore exports inaccurate, rounded values
    const divisions = Numeric.gcd(...finalState.measures.flatMap(m => m.notes.map(({ realValue }) => realValue != null ? realValue[1] : null)).filter(notEmpty));
    const measures = finalState.measures.map(measure => ({
        ...measure,
        notes: measure.notes.map(note => ({
            ...note, divisions,
            duration: note.realValue[0] * divisions / note.realValue[1]
        }))
    }));
    return sliceToSheets(measures).map(sliceToSegments);
}
function parseMeasure(parseState, measure) {
    const initialState = {
        parseState,
        measure: {
            timeSignature: parseState.timeSignature ?? { numerator: 4, denominator: 4 },
            element: measure,
            repeatStart: false,
            repeatEnd: false,
            newSystem: false,
            notes: []
        }
    };
    const finalState = measure.elements.reduce((state, element) => {
        if (element instanceof Attributes) {
            return reduceAttributes(state, element);
        }
        else if (element instanceof Direction) {
            return reduceDirection(state, element);
        }
        else if (element instanceof Barline) {
            return reduceBarline(state, measure, element);
        }
        else if (element instanceof Print) {
            return reducePrint(state, measure, element);
        }
        else if (element instanceof Note) {
            return reduceNote(state, element);
        }
        else if (element instanceof Backup) {
            return reduceBackup(state, element);
        }
        else if (element instanceof Forward) {
            return reduceForward(state, element);
        }
        else {
            throw new Error(`Unknown element: ${element}`);
        }
    }, initialState);
    return {
        ...finalState.parseState,
        measures: [...parseState.measures, finalState.measure]
    };
}
function extractNoteInfo(note) {
    const { value, realValue } = note;
    if (value == null && realValue == null) {
        throw new Error('Rest without value');
    }
    if (note.normalNotes == null || note.actualNotes == null) {
        throw new Error('Note without normal or actual notes');
    }
    const notes = [note.normalNotes, note.actualNotes];
    return { value, realValue, notes };
}
function aggregateChords(measure, instruments) {
    return measure.notes.reduce(foldChord, []);
    function foldChord(agg, note) {
        const { value, realValue, notes } = extractNoteInfo(note);
        if (note.isRest) {
            return restChord();
        }
        const hit = getHit(note, instruments);
        if (note.isChord) {
            return groupWithPrevious();
        }
        return [...agg, { value: value ?? realValue, realValue, notes, dots: note.dots, hits: [hit] }];
        function restChord() {
            if (note.isChord) {
                throw new Error('Rest with chord');
            }
            return [
                ...agg,
                {
                    value: value ?? realValue,
                    dots: note.dots,
                    realValue, notes, hits: []
                }
            ];
        }
        function groupWithPrevious() {
            const last = agg.at(-1);
            const past = agg.slice(0, -1);
            if (last == null) {
                throw new Error('Cannot start with chord');
            }
            if (last.hits.length == 0) {
                throw new Error('Chord after rest?');
            }
            if (!isEqual(last.value, value ?? realValue)) {
                throw new Error('Value mismatch in chord?');
            }
            if (!isEqual(last.realValue, realValue)) {
                throw new Error('Real Value mismatch in chord?');
            }
            if (!isEqual(last.notes, notes)) {
                throw new Error('Notes mismatch in chord?');
            }
            if (!isEqual(last.dots, note.dots)) {
                throw new Error('Notes mismatch in chord?');
            }
            return [...past, {
                    ...last,
                    hits: [...last.hits, hit]
                }];
        }
    }
}
function aggregateTuplets(chords) {
    return chords.reduce(foldTuplets, []);
    function foldTuplets(agg, chord) {
        const [last, past] = [agg.at(-1), agg.slice(0, -1)];
        if (last == null) {
            return startNew();
        }
        const { lastLength, lastNoteValue, lastTupletLength } = getCurrent(last);
        if (lastLength == lastTupletLength) {
            return startNew();
        }
        if (!isEqual(lastNoteValue, chord.notes)) {
            throw new Error('Tuplet notes mismatch');
        }
        return groupWithPrevious(last);
        function startNew() {
            return [...agg, {
                    value: chord.value,
                    noteValue: chord.notes,
                    notes: [{
                            realValue: chord.realValue,
                            dots: chord.dots,
                            hits: chord.hits
                        }]
                }];
        }
        function getCurrent(last) {
            const lastLength = last.notes.length;
            const lastNoteValue = last.noteValue;
            const lastTupletLength = lastNoteValue[1];
            if (lastLength > lastTupletLength) {
                throw new Error('Tuplet length mismatch');
            }
            return { lastLength, lastNoteValue, lastTupletLength };
        }
        function groupWithPrevious(last) {
            return [...past, {
                    ...last,
                    notes: [...last.notes, {
                            realValue: chord.realValue,
                            dots: chord.dots,
                            hits: chord.hits
                        }]
                }];
        }
    }
}
function getHit(note, instruments) {
    const { instrumentId, direction, isAccentuated } = note;
    if (instrumentId == null) {
        throw new Error('Instrument id is undefined for note');
    }
    const instrument = note.isRest
        ? null
        : instruments[instrumentId];
    if (instrument == null) {
        throw new Error(`Instrument with id ${instrumentId} not found`);
    }
    const pitch = instrument.unpitched;
    if (pitch == null) {
        throw new Error('Note without pitch');
    }
    const sticking = direction == null
        ? undefined
        : direction == 'L'
            ? 'L'
            : direction == 'R'
                ? 'R'
                : undefined;
    const dynamics = isAccentuated ? 'accent' : undefined;
    return {
        pitch: pitch - 1,
        ...(sticking ? { sticking } : {}),
        ...(dynamics ? { dynamics } : {})
    };
}
function reduceAttributes(state, attributes) {
    const divisions = attributes.divisions ?? state.parseState.divisions;
    const timeSignature = ((attributes.signature?.numerator && attributes.signature?.denominator)
        ? { numerator: attributes.signature.numerator, denominator: attributes.signature.denominator }
        : undefined) ?? state.parseState.timeSignature;
    return {
        ...state,
        parseState: { ...state.parseState, divisions, timeSignature },
        measure: timeSignature != null ? { ...state.measure, timeSignature } : state.measure
    };
}
function reduceDirection(state, direction) {
    if (state.direction != null) {
        throw new Error('Direction again');
    }
    return { ...state, direction };
}
function reduceBarline(state, measure, barline) {
    const repeatStart = barline.repeatStart;
    const repeatEnd = barline.repeatEnd;
    if (repeatStart && state.measure.repeatStart) {
        throw new Error('Repeat start already set');
    }
    if (repeatEnd && state.measure.repeatEnd) {
        throw new Error('Repeat and already set');
    }
    return {
        ...state,
        measure: {
            ...state.measure,
            repeatStart: state.measure.repeatStart || repeatStart,
            repeatEnd: state.measure.repeatEnd || repeatEnd
        }
    };
}
function reduceNote(state, note) {
    const value = note.value;
    const normalNotes = note.timeModification?.normalNotes ?? 1;
    const actualNotes = note.timeModification?.actualNotes ?? 1;
    const realValue = value != null
        ? [value[0], value[1] / normalNotes * actualNotes]
        : note.isMeasureRest
            ? state.parseState.timeSignature != null
                ? [state.parseState.timeSignature.numerator, state.parseState.timeSignature.denominator]
                : undefined
            : undefined;
    if (realValue == null) {
        throw new Error('No real value');
    }
    const parsedNote = {
        value, normalNotes, actualNotes, realValue,
        divisions: state.parseState.divisions,
        duration: note.duration,
        dots: note.dots,
        pitch: note.pitch,
        voice: note.voice,
        instrumentId: note.instrumentId,
        unpitched: note.unpitched,
        direction: state.direction?.text,
        isRest: note.isRest,
        isChord: note.isChord,
        isAccentuated: note.isAccentuated,
    };
    return {
        ...state,
        measure: {
            ...state.measure,
            notes: [...state.measure.notes, parsedNote]
        },
        direction: undefined
    };
}
function reducePrint(state, measure, print) {
    const isNewSystem = print.isNewSystem;
    if (isNewSystem && state.measure.newSystem) {
        throw new Error('New system already set');
    }
    return {
        ...state,
        measure: {
            ...state.measure,
            newSystem: state.measure.newSystem || isNewSystem
        }
    };
}
function reduceBackup(state, backup) {
    throw new Error('Backup not implemented');
}
function reduceForward(state, forward) {
    throw new Error('Forward not implemented');
}
function sliceToSheets(measures) {
    const finalState = measures.reduce(foldSheets, { finishedSheets: [], currentSheet: [] });
    return finalState.currentSheet.length > 0
        ? [...finalState.finishedSheets, finalState.currentSheet]
        : finalState.finishedSheets;
    function foldSheets(state, measure) {
        const { lastMeasure, currentSheet, finishedSheets } = state;
        const newNumberingStarted = lastMeasure != null &&
            (lastMeasure.element.number != null && measure.element.number != null &&
                lastMeasure.element.number >= measure.element.number);
        const newSystemStarted = measure.newSystem;
        return lastMeasure != null && (newNumberingStarted || newSystemStarted)
            ? { finishedSheets: [...finishedSheets, currentSheet], lastMeasure: measure, currentSheet: [measure] }
            : { finishedSheets, lastMeasure: measure, currentSheet: [...currentSheet, measure] };
    }
}
function sliceToSegments(measures) {
    const finalState = measures.reduce(foldSegments, { finishedSegments: [], currentSegment: [] });
    return finalState.currentSegment.length > 0
        ? [...finalState.finishedSegments, finalState.currentSegment]
        : finalState.finishedSegments;
    function foldSegments(state, measure) {
        const { lastMeasure, currentSegment, finishedSegments } = state;
        const timeSignatureChanged = lastMeasure != null && !isEqual(lastMeasure.timeSignature, measure.timeSignature);
        const repeatFinished = measure.repeatEnd;
        if (timeSignatureChanged) {
            return {
                finishedSegments: [...finishedSegments, currentSegment],
                lastMeasure: measure,
                currentSegment: [measure]
            };
        }
        else if (repeatFinished) {
            return {
                finishedSegments: [...finishedSegments, [...currentSegment, measure]],
                lastMeasure: measure,
                currentSegment: []
            };
        }
        else {
            return {
                finishedSegments,
                lastMeasure: measure,
                currentSegment: [...currentSegment, measure]
            };
        }
    }
}
//# sourceMappingURL=xmlToSheet.js.map